/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PackageItem} from "types/entities/PackageItem";
import {useDispatch} from "useDispatch";
import BookingAction from "store/booking/BookingAction";
import {Package} from "types/entities/Package";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useForm} from "react-hook-form";
import {getSelectedUpsell} from "utils/utility";
import {BookingProcess} from "types/app";
import {PackageUpsellItemContainer} from "pages/Front/Booking/components/PackageUpsell/PackageUpsellItemContainer";
import clsx from "clsx";
import {toast} from "react-toastify";
import {useTypedSelector} from "helpers/reducers";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {MinusIcon, PlusIcon, UsersIcon} from "@heroicons/react/24/outline";
import {UpsellOption, upsellOptions} from "pages/Front/Booking/components/PackageUpsell/UpsellOptions";
import {CheckCircleIcon} from "@heroicons/react/24/solid";

interface Params {
    package_id: string;
}

interface PackageUpsellOptionItemProps extends RouteComponentProps<Params> {
    packageItem: PackageItem;
    selectedPackage: Package | null;
    contained: boolean;
    disabled?: boolean;
    bookingProcess: BookingProcess;
    zIndex: any;
    groupedPackageItems?: object | any;
    selectedGroupedPackage?: [] | null;
    // maxPersonCount?: number;
}

const PackageUpsellOptionItem = (props: PackageUpsellOptionItemProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    // const isPackageInGroupedPackageItems = props.packageItem?.extra?.article_group_id && props?.groupedPackageItems && props.groupedPackageItems[props.packageItem?.extra?.article_group_id].length > 0;
    const groupedPackageItems: PackageItem[] =
        props.packageItem?.extra?.article_group_id && props?.groupedPackageItems ? props.groupedPackageItems[props.packageItem?.extra?.article_group_id] : [];
    const groupedPackageItemsCount = groupedPackageItems.length;
    const isPackageInGroupedPackageItems = groupedPackageItemsCount > 0;
    // const firstGroupedPackageItem: PackageItem | null = (isPackageInGroupedPackageItems && props.packageItem?.extra?.article_group_id) ? props.groupedPackageItems[props.packageItem?.extra?.article_group_id][0] : null;
    // const isFirstGroupedPackageItem: boolean = firstGroupedPackageItem?.id === props.packageItem.id;
    // const allGroupedPackages = Object.values(groupedPackageItemsByArticleGroupId);
    // const isPackageGroupedByArticle: boolean = groupedPackageItemsByArticleGroupId && allGroupedPackages.length === 1;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {register, watch, setValue} = useForm();
    const adultCount = watch("adult-count");
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const maxPersonsCount: number = +bookingFilter.adults + +bookingFilter.children;
    // const persons = +bookingFilter.adults + +bookingFilter.children;
    // const minPersons = props.packageItem.minimum_persons_count;
    const minPersons = isPackageInGroupedPackageItems ? 0 : props.packageItem.minimum_persons_count;
    const minimumValue = props.packageItem.extra.whole_group ? (maxPersonsCount < minPersons ? minPersons : maxPersonsCount) : minPersons;
    const [count, setCount] = useState<number>(minimumValue || 0);

    const selectedUpsell = getSelectedUpsell(props.bookingProcess.upsell, props.packageItem);
    const isSelectedUpsell: boolean = selectedUpsell !== undefined;
    const allBookedUpsellsInGroup = props.bookingProcess.upsell.filter(
        (upsell, index) => upsell.packageItem?.extra?.article_group_id === props.packageItem.extra?.article_group_id
    );
    // const allBookedUpsellsCount = allBookedUpsellsInGroup
    //     .reduce((accumulator, value) => accumulator + value.count, 0)
    // ;

    const isDisableChangePersonCount: boolean = isPackageInGroupedPackageItems && groupedPackageItemsCount === 1;
    const isMaxPersonCountProductItemGrouped = selectedUpsell ? selectedUpsell.count === maxPersonsCount : count === maxPersonsCount;
    const isMinPersonCountProductItemGrouped = selectedUpsell ? selectedUpsell.count === minPersons : count === minPersons;
    const classNameButton =
        "h-10 w-10 text-contrast-800 rounded-md hover:no-underline text-3xl flex-grow text-center flex flex-row justify-center items-center";
    const classNameButtonDisabled = "bg-gray-700 cursor-not-allowed pointer-events-none select-none";
    const classNameButtonEnabled = "bg-cta-800 border-cta-800 border bg-opacity-90 cursor-pointer";
    const classNameButtonGroupedProductItemMax = isMaxPersonCountProductItemGrouped ? classNameButtonDisabled : classNameButtonEnabled;
    const classNameButtonGroupedProductItemMin = isMinPersonCountProductItemGrouped ? classNameButtonDisabled : classNameButtonEnabled;

    const [showRemoveButton, setShowRemoveButton] = useState<boolean>(getSelectedUpsell(props.bookingProcess.upsell, props.packageItem) !== undefined);
    const [callingToAction, setCallingToAction] = useState<boolean>(false);
    const [upsellOption, setUpsellOption] = useState<number>(props.packageItem.extra.upsell_option);

    const setOptionNoteHandler = (option: UpsellOption) => {
        setValue("upsellOption", option.id);
        setUpsellOption(option.id);
        dispatch(
            BookingAction.pushUpsell({
                packageItem: props.packageItem,
                upsell_option: option.id,
                count: Number(count),
            })
        );
    };

    const setSelectedPackageUpsell = (): void => {
        const currentUpsell = getSelectedUpsell(props.bookingProcess.upsell, props.packageItem);
        if (showRemoveButton && currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell));
            // toast.warning(t("Item removed"));
            setShowRemoveButton(false);
            return;
        }
        let newCount = props.packageItem.extra.max_count === 1 ? 1 : count;

        if (maxCount != null) {
            if (newCount > maxCount) {
                newCount = maxCount;
            }
        }

        if (props.packageItem.extra.group) {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: props.packageItem,
                    upsell_option: upsellOption,
                    count: Number(newCount),
                })
            );
            setShowRemoveButton(true);
            // toast.success(t("Item added"));
            return;
        }
        if (count == 0 && currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell));
        }
        if (count > 0) {
            // toast.success(t("Item added"));
        }
        dispatch(
            BookingAction.pushUpsell({
                packageItem: props.packageItem,
                upsell_option: upsellOption,
                count: Number(newCount),
            })
        );
        //upsell package goes before book
        // props.history.push("/booking/book/" + PackageItem.package_id );
    };

    const maxCount = props.packageItem.extra.max_count;
    const minimumValueHandler = e => {
        const enteredValue = e.target.value;
        if (enteredValue < minimumValue) {
            setValueHandler(minimumValue);
            toast.error(t("cannot order less", {minimum_value: minimumValue}));
            return;
        }
        setValueHandler(enteredValue);
    };

    const minimumValueGroupedHandler = e => {
        const enteredValue = e.target.value;
        // if (enteredValue < minPersons) {
        //     setValueHandler(+minPersons);
        //     toast.error(t("cannot order less", {minimum_value: minimumValue}));
        //     return;
        // }
        // if (enteredValue > maxPersonsCount) {
        //     setValueHandler(+maxPersonsCount);
        //     toast.error(t("cannot order more", {minimum_value: minimumValue}));
        //     return;
        // }
        setDriversCountToGroupedUpsellHandler(+enteredValue, true);
        // setValueHandler(+enteredValue);
    };

    const setValueHandler = value => {
        setCount(value);
        setCallingToAction(true);
        dispatch(BookingAction.setUpsell({packageItem: props.packageItem, upsell_option: upsellOption, count: value}));
    };

    useEffect(() => {
        setCount(minimumValue);
        if (minimumValue > count) {
            setCount(minimumValue);
        }
        if (maxCount != null) {
            if (count > maxCount) {
                setCount(maxCount);
            }
        }
        return () => {};
    }, [minimumValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCallingToAction(false);
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [callingToAction]);

    const valueButtonClassName = (props.contained && !showRemoveButton ? classNameButtonDisabled : classNameButtonEnabled) + classNameButton;

    const setDriversCountToGroupedUpsellHandler = (newPersonCount: number, personCountFromInputValue?: boolean) => {
        const newPersonsCountToSet = personCountFromInputValue ? +newPersonCount : selectedUpsell ? selectedUpsell.count + newPersonCount : newPersonCount;

        if (newPersonsCountToSet <= maxPersonsCount && newPersonsCountToSet >= minPersons && groupedPackageItemsCount > 1) {
            personCountFromInputValue
                ? changePersonCountFromInputValue(newPersonsCountToSet)
                : changePersonCountFromButton(newPersonsCountToSet, newPersonCount);
        }
    };

    const changePersonCountFromInputValue = (newPersonCount: number) => {
        const anotherUpsellBooked = allBookedUpsellsInGroup
            .filter(upsellItem => upsellItem.packageItem.id !== props.packageItem.id)
            .sort((a, b) => b.count - a.count);
        const anotherUpsell = groupedPackageItems.find(packageItem => packageItem.id !== props.packageItem.id);
        const allBookedPersonCount = anotherUpsellBooked.reduce((accumulator, newCount) => accumulator + newCount.count, 0);
        let personsCountTmp = maxPersonsCount + (allBookedPersonCount - newPersonCount);

        if (anotherUpsellBooked.length > 0) {
            anotherUpsellBooked.map(bookedUpsell => {
                if (personsCountTmp < 0) return;

                const countForBooked = bookedUpsell.count - personsCountTmp;
                personsCountTmp = countForBooked > -1 ? countForBooked : -countForBooked;
                if (countForBooked > 0) {
                    BookingAction.pushUpsell({
                        packageItem: bookedUpsell.packageItem,
                        upsell_option: bookedUpsell.packageItem.extra.upsell_option,
                        count: countForBooked,
                    });
                } else {
                    dispatch(BookingAction.removeUpsell(bookedUpsell));
                }
            });
        }

        if (anotherUpsell && personsCountTmp > 0) {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: anotherUpsell,
                    upsell_option: anotherUpsell.extra.upsell_option,
                    count: personsCountTmp,
                })
            );
        }

        if (newPersonCount === 0) {
            selectedUpsell && dispatch(BookingAction.removeUpsell(selectedUpsell));
        } else {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: props.packageItem,
                    upsell_option: props.packageItem.extra.upsell_option,
                    count: newPersonCount,
                })
            );
        }
    };

    const changePersonCountFromButton = (newPersonsCountInSelectedPackageItem: number, newPersonCount: number) => {
        const anotherUpsellBooked = allBookedUpsellsInGroup.find(upsellItem => upsellItem.packageItem.id !== props.packageItem.id);
        const anotherUpsell = groupedPackageItems.find(packageItem => packageItem.id !== props.packageItem.id);

        if (anotherUpsellBooked) {
            const newPersonsCountForAnotherUpsell = anotherUpsellBooked.count - newPersonCount;
            if (newPersonsCountForAnotherUpsell === 0) {
                dispatch(BookingAction.removeUpsell(anotherUpsellBooked));
            } else {
                dispatch(
                    BookingAction.pushUpsell({
                        packageItem: anotherUpsellBooked.packageItem,
                        upsell_option: anotherUpsellBooked.packageItem.extra.upsell_option,
                        count: newPersonsCountForAnotherUpsell,
                    })
                );
            }
        } else if (anotherUpsell) {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: anotherUpsell,
                    upsell_option: anotherUpsell.extra.upsell_option,
                    count: maxPersonsCount - newPersonsCountInSelectedPackageItem,
                })
            );
        }

        if (newPersonsCountInSelectedPackageItem === 0) {
            selectedUpsell && dispatch(BookingAction.removeUpsell(selectedUpsell));
        } else {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: props.packageItem,
                    upsell_option: props.packageItem.extra.upsell_option,
                    count: newPersonsCountInSelectedPackageItem,
                })
            );
        }
    };

    function setDriversCountHandler(number: number) {
        console.log("setDriversCountHandler", number);
        const newCount = +count + number;
        if (props.packageItem.maximum_persons_count > 0 && newCount > props.packageItem.maximum_persons_count) {
            return;
        }
        if (newCount >= 0) {
            setValueHandler(newCount);
        }

        if (newCount < minimumValue) {
            setValueHandler(minimumValue);
            // toast.error(t("cannot order less", {minimum_value: minimumValue}));
            return;
        }
    }

    const setInitPackageGrouped = (newUpsell: PackageItem, personsCount: number) => {
        dispatch(
            BookingAction.pushUpsell({
                packageItem: newUpsell,
                upsell_option: newUpsell.extra.upsell_option,
                count: personsCount,
            })
        );
    };

    // set first grouped product
    useEffect(() => {
        if (
            isPackageInGroupedPackageItems &&
            props?.selectedGroupedPackage &&
            props.selectedGroupedPackage.length === 0 &&
            props?.packageItem?.extra?.article_group_id &&
            props.groupedPackageItems[props.packageItem.extra.article_group_id][0].id == props.packageItem.id
        ) {
            setInitPackageGrouped(props.packageItem, maxPersonsCount);
        }
    }, []);

    const groupedInputsRender = (
        <div className={clsx("UpsellInputs flex flex-col justify-center items-center flex-grow mb-2 z-1")}>
            <div className={"flex flex-row justify-center items-center gap-2"}>
                <span className={"sr-only"}>{t("Count")}</span>
                <button
                    className={clsx(isDisableChangePersonCount ? classNameButtonDisabled : classNameButtonGroupedProductItemMin, classNameButton)}
                    type={"button"}
                    onClick={() => setDriversCountToGroupedUpsellHandler(-1)}
                >
                    <MinusIcon className={"w-5 h-5"} />
                </button>
                <div className={"relative"}>
                    <input
                        {...register("adult-count")}
                        className={
                            "font-bold border-2 rounded-md border-gray-500 w-20 text-gray-900 pl-8 focus:pl-0 placeholder-gray-700 text-center text-lg py-1"
                        }
                        data-count={minimumValue}
                        // defaultValue={selectedUpsell && selectedUpsell.count}
                        // disabled={showRemoveButton}
                        max={maxPersonsCount}
                        min={minPersons}
                        name={"adult-count"}
                        type={"number"}
                        value={selectedUpsell ? selectedUpsell.count : count}
                        onChange={minimumValueGroupedHandler}
                    />
                    <div className={" absolute inset-y-0 left-4 flex items-center"}>
                        <UsersIcon className={"w-4 h-4 text-gray-700"} />
                    </div>
                </div>
                <button
                    className={clsx(isDisableChangePersonCount ? classNameButtonDisabled : classNameButtonGroupedProductItemMax, classNameButton)}
                    type={"button"}
                    onClick={() => setDriversCountToGroupedUpsellHandler(+1)}
                >
                    <PlusIcon className={"w-5 h-5"} />
                </button>
            </div>
        </div>
    );

    const basicInputsRender = (
        <div className={clsx("UpsellInputs  flex flex-col justify-center items-center flex-grow mb-2 z-1")}>
            {/*{props.packageItem.extra.whole_group && (*/}
            {/*    <input  {...register("adult-count")} name={"adult-count"} type={"hidden"} />*/}
            {/*)}*/}
            {props.packageItem.extra.max_count === 1 && (
                <input {...register("adult-count")} name={"adult-count"} type={"hidden"} value={props.packageItem.extra.max_count} />
            )}

            {props.packageItem.extra.max_count !== 1 && (
                <label className={"flex flex-row justify-center items-center gap-2"}>
                    <span className={"sr-only"}>{t("Count")}</span>
                    <button className={valueButtonClassName} type={"button"} onClick={() => setDriversCountHandler(-1)}>
                        <MinusIcon className={"w-5 h-5"} />
                    </button>
                    {/*<span className={"font-medium"}>{!props.packageItem.person_type ? t("Extra item") : t("Extra games count")}</span>*/}
                    <div className={"relative"}>
                        <input
                            {...register("adult-count")}
                            className={
                                "font-bold border-2 rounded-md border-gray-500 w-20 text-gray-900 pl-8 focus:pl-0 placeholder-gray-700 text-center text-lg py-1"
                            }
                            data-count={minimumValue}
                            disabled={showRemoveButton}
                            max={props.packageItem.extra.max_count}
                            min={minimumValue}
                            name={"adult-count"}
                            placeholder={
                                props.packageItem.person_type === PackageItemPersonType.Adult ? "" + bookingFilter.adults : "" + bookingFilter.children
                            }
                            type={"number"}
                            value={count}
                            onChange={minimumValueHandler}
                        />
                        <div className={" absolute inset-y-0 left-4 flex items-center"}>
                            <UsersIcon className={"w-4 h-4 text-gray-700"} />
                        </div>
                    </div>
                    <button className={valueButtonClassName} type={"button"} onClick={() => setDriversCountHandler(+1)}>
                        <PlusIcon className={"w-5 h-5"} />
                    </button>
                </label>
            )}
        </div>
    );

    return (
        <PackageUpsellItemContainer
            callToAction={callingToAction}
            className={"PackageUpsellOptionItem"}
            contained={props.contained}
            inputs={isPackageInGroupedPackageItems ? groupedInputsRender : basicInputsRender}
            isGroupedProduct={isPackageInGroupedPackageItems}
            isLoading={isLoading}
            packageItem={props.packageItem}
            setSelectedPackageUpsell={setSelectedPackageUpsell}
            showRemoveButton={showRemoveButton}
            style={{
                zIndex: (100 - props.zIndex) * 10,
            }}
        >
            {props.packageItem.extra.upsell_option !== 0 && props.packageItem.extra.upsell_option !== null && (
                <section
                    className={clsx(
                        showRemoveButton ? "opacity-100 max-h-[8rem]" : "opacity-20 max-h-[0px]  pointer-events-none",
                        "UpsellNote transform-gpu duration-500 ease-in-out overflow-hidden w-full grow"
                    )}
                    style={{
                        zIndex: (100 - props.zIndex) * 10 - 2,
                    }}
                >
                    <input type={"hidden"} {...register("optionNote")} />
                    <div className={" shadow bg-white p-3 border border-t-gray-500 space-y-4 pb-2"}>
                        <h3 className={"leading-4 text-sm"}>{t("When do you want to have this option?")}</h3>
                        <div className={"UpsellOptions flex flex-row space-x-2 sm:space-x-4"}>
                            {upsellOptions.map(upsellOptionItem => {
                                return (
                                    <button
                                        key={upsellOptionItem.id}
                                        className={clsx(
                                            upsellOption === upsellOptionItem.id
                                                ? " border border-cta-800 bg-cta-800 text-contrast-100 "
                                                : " border border-cta-800 bg-white text-contrast-100 ",
                                            "px-4 grow text-xs leading-3 sm:text-base rounded-lg p-1 relative"
                                        )}
                                        onClick={() => setOptionNoteHandler(upsellOptionItem)}
                                    >
                                        <div className={"optionSelected absolute inset-y-0 left-0 sm:left-2 flex items-center"}>
                                            {upsellOption === upsellOptionItem.id && <CheckCircleIcon className={"w-4 h-4 text-white"} />}
                                        </div>
                                        <span className={"first-letter:capitalize"}>{t(upsellOptionItem.option + " event")}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}
        </PackageUpsellItemContainer>
    );
};

export default withRouter(PackageUpsellOptionItem);
